import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LockIcon from "@mui/icons-material/Lock";
import { branches } from "../../WebleadsV2/utilities";
import { PageHeader, PageShell } from "../../../components/UI/Page";

// Also used by the Order Problems dashboard analytics to tell these fixed
// types apart from custom write-ins when grouping.
export const PROBLEM_TYPES = [
  "Job Not Ready",
  "Refused by Customer",
  "Product Left in Truck",
  "Wrong Product Delivered",
  "Damaged Product",
  "Missing Product",
  "Special Instruction Not Followed",
  "Delivered to Wrong Job",
  "Wrong Product on Order",
  "Job Not Found",
];

// Photos are stored on the problem document as base64 data URLs (same as the
// driver app), so compress client-side to the driver app's per-photo cap.
const MAX_PHOTOS = 3;
const MAX_DATA_URL_LENGTH = 650000;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      let quality = 0.8;
      for (let attempt = 0; attempt < 8; attempt++) {
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        if (dataUrl.length <= MAX_DATA_URL_LENGTH) return resolve(dataUrl);
        if (quality > 0.5) quality -= 0.15;
        else scale *= 0.7;
      }
      reject(new Error("photo could not be compressed small enough"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not read that image"));
    };
    img.src = url;
  });

const SectionLabel = ({ children, hint, required }) => (
  <div>
    <p className="text-sm font-semibold">
      {children}
      {required && (
        <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
      )}
    </p>
    {hint && <p className="app-muted mt-0.5 text-xs">{hint}</p>}
  </div>
);

const Section = ({ children, className = "" }) => (
  <section className={`app-card rounded-2xl border p-5 ${className}`}>
    {children}
  </section>
);

// Reports are submitted anonymously — nothing identifying the submitter is
// collected or sent, so the dashboard shows them as Anonymous and nobody is
// notified about replies or status changes.
const OrderProblemForm = () => {
  const [salesorder, setSalesorder] = useState("");
  const [branch, setBranch] = useState("");
  const [problemTypes, setProblemTypes] = useState([]);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherProblemType, setOtherProblemType] = useState("");
  const [comments, setComments] = useState("");
  const [photos, setPhotos] = useState([]); // { name, dataUrl }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handlePhotoChange = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setSubmitError(null);

    const room = MAX_PHOTOS - photos.length;
    if (picked.length > room) {
      setSubmitError(`You can attach up to ${MAX_PHOTOS} photos.`);
    }
    for (const file of picked.slice(0, Math.max(0, room))) {
      try {
        const dataUrl = await compressImage(file);
        setPhotos((prev) =>
          prev.length >= MAX_PHOTOS
            ? prev
            : [...prev, { name: file.name, dataUrl }]
        );
      } catch (err) {
        setSubmitError(`Couldn't add "${file.name}" — ${err.message}.`);
      }
    }
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleProblemType = (value) => {
    setProblemTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const addOtherProblemType = () => {
    const trimmed = otherProblemType.trim();
    if (trimmed && !problemTypes.includes(trimmed)) setProblemTypes([...problemTypes, trimmed]);
    setOtherProblemType("");
    setOtherOpen(false);
  };

  const canSubmit = branch && problemTypes.length > 0 && !submitting;

  const resetForm = () => {
    setSalesorder("");
    setBranch("");
    setProblemTypes([]);
    setComments("");
    setPhotos([]);
    setSubmitError(null);
    setSubmitted(false);
  };

  const submitForm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch("/api/forms/submit/orderProblem", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          branch,
          orderNumber: salesorder.trim(),
          errors: problemTypes,
          comments: comments.trim(),
          images: photos.map((p) => ({
            data: p.dataUrl,
            name: p.name,
            mimeType: "image/jpeg",
          })),
        }),
      });
      const content = await res.json();
      if (content.success === 1) {
        setSubmitted(true);
      } else {
        setSubmitError(
          content.message || "Something went wrong — please try again."
        );
      }
    } catch (err) {
      setSubmitError("Could not submit the form — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Why Submit is unavailable, said out loud instead of a silent grey button.
  const missingRequired = !branch || problemTypes.length === 0;

  if (submitted) {
    return (
      <PageShell size="sm" className="sm:py-12">
        <div className="app-card flex flex-col items-center rounded-2xl border px-6 py-10 text-center sm:px-10">
          <span className="app-icon-tile h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <TaskAltIcon fontSize="large" />
          </span>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            Report submitted!
          </h1>
          <p className="app-muted mt-2 max-w-md text-sm leading-relaxed">
            Thanks for the heads-up — your report is now on the Order Problems
            dashboard for the team to work. It was submitted anonymously, so
            there's no reply coming back to you.
          </p>
          <Button variant="contained" className="!mt-6" onClick={resetForm}>
            Submit Another
          </Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell size="sm">
      <PageHeader
        icon={ReportProblemIcon}
        tone="rose"
        eyebrow="Forms"
        title="Order Problem Report"
        description="Report an order problem so we can make it right for the customer."
      />

      <div className="space-y-5">
        {/* Order + branch */}
        <Section>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <SectionLabel hint="Leave blank if there's no order tied to this problem.">
                Sales Order Number
              </SectionLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="Sales Order #"
                value={salesorder}
                onChange={(e) => setSalesorder(e.target.value)}
                inputProps={{ "aria-label": "Sales Order Number" }}
              />
            </div>
            <div className="space-y-2">
              <SectionLabel hint="Which branch does this involve?" required>
                Branch
              </SectionLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                SelectProps={{
                  displayEmpty: true,
                  SelectDisplayProps: { "aria-label": "Branch" },
                }}
              >
                <MenuItem value="" disabled>
                  Select a branch...
                </MenuItem>
                {branches
                  .filter((b) => b !== "All Locations")
                  .map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
              </TextField>
            </div>
          </div>
        </Section>

        {/* Error types */}
        <Section className="space-y-3">
          <SectionLabel hint="Select everything that applies." required>
            What went wrong?
          </SectionLabel>
          <div className="grid gap-2 sm:grid-cols-2">
            {PROBLEM_TYPES.map((type) => {
              const selected = problemTypes.includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleProblemType(type)}
                  className={`flex min-h-[2.75rem] items-start gap-2 rounded-xl border p-3 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                    selected
                      ? "border-sky-500 bg-sky-500/10 font-medium dark:border-sky-400"
                      : "app-surface hover:border-sky-400"
                  }`}
                >
                  {selected ? (
                    <CheckCircleIcon
                      className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-300"
                      sx={{ fontSize: 18 }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      className="app-muted mt-0.5 shrink-0"
                      sx={{ fontSize: 18 }}
                    />
                  )}
                  <span>{type}</span>
                </button>
              );
            })}
            {/* Custom entries appear alongside the presets */}
            {problemTypes
              .filter((e) => !PROBLEM_TYPES.includes(e))
              .map((type) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed="true"
                  onClick={() => toggleProblemType(type)}
                  className="flex min-h-[2.75rem] items-start gap-2 rounded-xl border border-sky-500 bg-sky-500/10 p-3 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-sky-400"
                >
                  <CheckCircleIcon
                    className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-300"
                    sx={{ fontSize: 18 }}
                  />
                  <span>{type}</span>
                </button>
              ))}
            {otherOpen ? (
              <div className="flex items-center gap-1 rounded-xl border border-dashed border-[color:var(--app-input-border)] p-2">
                <TextField
                  fullWidth
                  size="small"
                  autoFocus
                  placeholder="Describe the problem type"
                  value={otherProblemType}
                  onChange={(e) => setOtherProblemType(e.target.value)}
                  inputProps={{ "aria-label": "Describe the problem type" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOtherProblemType();
                    }
                    if (e.key === "Escape") setOtherOpen(false);
                  }}
                />
                <Button size="small" onClick={addOtherProblemType}>
                  Add
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setOtherOpen(true)}
                className="app-muted flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border border-dashed border-[color:var(--app-input-border)] p-3 text-sm font-medium transition-colors hover:border-sky-400 hover:text-[color:var(--app-text)]"
              >
                <AddIcon sx={{ fontSize: 18 }} /> Other
              </button>
            )}
          </div>
        </Section>

        {/* Comments */}
        <Section className="space-y-3">
          <SectionLabel hint="Anything that would help resolve this for the customer.">
            Additional Comments
          </SectionLabel>
          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Explain what happened..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            inputProps={{ "aria-label": "Additional Comments" }}
          />
        </Section>

        {/* Photos */}
        <Section className="space-y-3">
          <SectionLabel hint="Optional — up to 3 photos of the damage, paperwork, or jobsite help resolve things faster.">
            Photos
          </SectionLabel>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              component="label"
              variant="outlined"
              disabled={photos.length >= MAX_PHOTOS}
              startIcon={<AddPhotoAlternateIcon />}
            >
              Add Photos
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handlePhotoChange}
              />
            </Button>
            <span className="app-muted text-xs tabular-nums">
              {photos.length} of {MAX_PHOTOS}
            </span>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((photo, index) => (
                <div
                  key={`${photo.name}-${index}`}
                  className="app-surface relative aspect-square overflow-hidden rounded-xl border"
                >
                  <img
                    src={photo.dataUrl}
                    alt={photo.name}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                    aria-label={`Remove ${photo.name}`}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {submitError && (
          <div
            role="alert"
            className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
          >
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div className="app-card flex flex-col gap-3 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="app-muted flex items-center gap-1.5 text-sm">
              <LockIcon sx={{ fontSize: 16 }} />
              Submitted anonymously — your name isn't attached to this report.
            </p>
            {missingRequired && (
              <p className="app-muted text-xs">
                Pick a branch and at least one problem type to submit.
              </p>
            )}
          </div>
          <Button
            variant="contained"
            size="large"
            disabled={!canSubmit}
            onClick={submitForm}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {submitting ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </div>
    </PageShell>
  );
};

export default OrderProblemForm;
